import { inject, Injectable } from '@angular/core';
import { appendToRmaStaticImageUrl } from '@rma/generic/ng/util';
import { AwardType, ProfileRecognitionBadges } from '@rma/public/generic/domain/types';
import { AccomplishmentDetails } from '../domain/accomplishment-details.model';

@Injectable({
  providedIn: 'root',
})
export class AccomplishmentMapperService {
  private readonly rmaImagesUrl = inject(appendToRmaStaticImageUrl);

  private awardColors = new Map<AwardType, string>([
    ['Country', 'light-green'],
    ['State', 'light-purple'],
    ['City', 'light-purple'],
    ['Locality', 'light-orange'],
    ['StateRegion', 'light-orange'],
  ]);

  public map(data: ProfileRecognitionBadges): AccomplishmentDetails[] {
    let order = 1;
    const recognitionItems: AccomplishmentDetails[] = [];

    if (!data) {
      return recognitionItems;
    }

    if (data.isHighlyRecommended) {
      recognitionItems.push({
        order: order++,
        titleTranslationKey: 'agent.badge.highly-recommended',
        descriptionTranslationKey: 'agent.badge.highly-recommended.description',
        badge: { src: this.getAssetsUrl('recognition-top20'), color: 'white' },
      });
    }

    if (data.isTrustedAgent) {
      recognitionItems.push({
        order: order++,
        titleTranslationKey: 'agent.badge.trusted-agent',
        descriptionTranslationKey: 'agent.badge.trusted-agent.description',
        badge: {
          src: this.getAssetsUrl('recognition-trusted'),
          color: 'white',
        },
      });
    }

    if (data.isPriceExpert) {
      recognitionItems.push({
        order: order++,
        titleTranslationKey: 'agent.badge.priceExpert',
        descriptionTranslationKey: 'agent.badge.priceExpertDescription',
        badge: {
          src: this.getAssetsUrl('recognition-priceExpert'),
          color: 'white',
        },
      });
    }

    if (data.repeatAwards && data.repeatAwards.length > 0) {
      const aoty = data.repeatAwards.find((award) => award.awardType === 'Country' && award.winType === 'Winner');

      if (aoty) {
        // If agent has won Agent Of The Year (National), place it at the top regardless of other ordering criteria
        const color = this.awardColors.get(aoty.awardType) ?? 'light-orange';

        recognitionItems.push({
          order: order++,
          customTitle: {
            numberOfYears: aoty.winningYears.length,
            awardTypeTranslationKey: 'award-type.aoty',
          },
          subtitle: aoty.awardName,
          descriptionTranslationKey: `${aoty.winningYears.join(', ')}`,
          badge: { src: this.getAwardAssetUrl(aoty.awardType), color },
        });
      }

      data.repeatAwards
        .filter((award) => !(award.awardType === 'Country' && award.winType === 'Winner'))
        .forEach((award) => {
          const color = this.awardColors.get(award.awardType) ?? 'light-orange';

          const awardTypeTranslationKey = `award-type.${this.toKebabCase(award.awardType.toString())}`;

          const rankingTranslationKey = award.winType === 'Winner' ? 'award-type.winner' : 'award-type.top-of';

          recognitionItems.push({
            order: order++,
            customTitle: {
              numberOfYears: award.winningYears.length,
              awardTypeTranslationKey,
              rankingTranslationKey,
              rankedOutOf: award.rankedOutOf,
            },
            subtitle: award.awardName,
            descriptionTranslationKey: `${award.winningYears.join(', ')}`,
            badge: { src: this.getAwardAssetUrl(award.awardType), color },
          });
        });
    }

    return recognitionItems;
  }

  private getAssetsUrl(name: string): string {
    return this.rmaImagesUrl(`recognition/${name}.svg`);
  }

  private getAwardAssetUrl(awardType: AwardType): string {
    const typeString = awardType.toString().toLowerCase();
    return this.rmaImagesUrl(`profile-badges/awards-${typeString}-trophy.svg`);
  }

  private toKebabCase(str: string): string {
    if (!str) {
      return '';
    }

    const matches = str.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g);
    if (matches) {
      return matches.map((x) => x.toLowerCase()).join('-');
    }

    return '';
  }
}
